interface DateConstructor
{
	/** This method returns today date */
	today(): Date;

	/** This method correctly converts a Date to avoid time errors.
	 * @param dateFromServer date passed from server to client.
	 * @returns Returns a date.
	 * @remarks Prefer VrDateTime for new code; kept for backward compatibility. */
	convertDateFromServer(dateFromServer: Date): Date;

	/** This method correctly converts a Date to avoid time errors.
	 * @param dateFromClient date passed from client to server.
	 * @returns Returns a date.
	 * @remarks Prefer VrDateTime for new code; kept for backward compatibility. */
	convertDateFromClient(dateFromClient?: Date | null): Date;

	/**
	 * This method fixes dates that might be strings, always producing an actual date.
	 * @param dateString The date to fix.
	 */
	fixDateString(dateString?: Date | string | null): Date;

	/** This method returns the difference (in milliseconds) between two dates. */
	differenceBetweenDatesInMilliseconds(firstDate?: Date | null, secondDate?: Date | null): number;

	/** This method returns the difference (in seconds) between two dates. */
	differenceBetweenDatesInSeconds(firstDate?: Date | null, secondDate?: Date | null): number;

	/** This method returns the difference (in minutes) between two dates. */
	differenceBetweenDatesInMinutes(firstDate?: Date | null, secondDate?: Date | null): number;

	/** This method returns the difference (in days) between two dates. */
	differenceBetweenDatesInDays(firstDate?: Date | null, secondDate?: Date | null): number;

	/** This method returns the first day of week by date. */
	getFirstDayOfWeekByDate(dateToCheck: Date): Date;

	/** This method returns the first day of month by date. */
	getFirstDayOfMonthByDate(dateToCheck: Date): Date;

	/** This method returns the last day of month by date. */
	getLastDayOfMonthByDate(dateToCheck: Date): Date;

	/** This method returns the first day of the year. */
	getFirstDayOfYear(): Date;

	/** This method returns the last day of the year. */
	getLastDayOfYear(): Date;

	/** This method returns true if two dates are equal */
	equals(firstDate: Date, secondDate: Date, ignoreHours?: boolean): boolean;

	/** This method returns the sundays of the month */
	getSundaysOfMonthByDate(dateToCheck: Date): Date[];

	/** This method returns the name of the month */
	getMonthNameByDate(date: Date): string;

	/** This method returns the days in a month */
	getDaysInMonth(date: Date): number;

	/** Know if a period of dates are overlapped (A = firstPeriod, B = secondPeriod)
	 * @param startA = start of the first period
	 * @param endA = end of the first period
	 * @param startB = start of the second period
	 * @param endB = end of the second period*/
	arePeriodsOverlapped(startA: Date, endA: Date, startB: Date, endB: Date, equal?: boolean): boolean;

	/** Return if object is a valid date. */
	isValidDate(object: any): boolean;

	MIN_VALUE: Date;
	MAX_VALUE: Date;
}

interface Date
{
	/** This method turns date into a string in this format: dd/mm/yyyy.
	* @returns Returns a string in this format: dd/mm/yyyy. */
	toItalyShortString(): string;

	toItalyString(mode?: vr.DateModeEnum, showSeconds?: boolean): string;

	toLongDateString(): string;

	formatString(options: Intl.DateTimeFormatOptions, language?: string[] | string): string;

	/** This method turns date into a string in this format: hh:MM.
	* @returns Returns a string in this format: hh:MM. */
	toTimeString(seconds?: boolean): string;

	/** This method add years to a Date.
	* @returns Returns a Date */
	addYears(years: number): Date;

	/** This method add months to a Date.
	* @returns Returns a Date */
	addMonths(months: number): Date;

	/** This method add days to a Date.
	* @returns Returns a Date */
	addDays(days: number): Date;

	/** This method add hours (negative too) to a Date.
	* @returns Returns a Date */
	addHours(hours: number): Date;

	/** This method add minutes (negative too) to a Date.
	* @returns Returns a Date */
	addMinutes(minutes: number): Date;

	/** This method add seconds (negative too) to a Date.
	* @returns Returns a Date */
	addSeconds(seconds: number): Date;

	/** This method says if a date is between two dates. */
	isBetween(firstDate: Date, secondDate: Date): boolean;

	/** Gets the first day of the date's month. */
	getMonthBegin(): Date;

	/** This method says if a date is less than other. */
	isLessThan(date: Date, equals?: boolean): boolean;

	/** This method says if a date is greater than other. */
	isGreaterThan(date: Date, equals?: boolean): boolean;

	/** This method says if a date is equal than other. */
	isEqualsTo(date: Date, ignoreHours?: boolean): boolean;

	/** This method turns date into a VrDateTime. */
	toVrDateTime<T>(): T;

	/** This method turns date into a VRString. */
	toVrString(mode?: vr.DateModeEnum, showSeconds?: boolean): string;
}


//#region Static
Date.MIN_VALUE = new Date(-8640000000000000);
Date.MAX_VALUE = new Date(8640000000000000);

Date.convertDateFromServer = function (dateFromServer: Date): Date
{
	if (dateFromServer != null)
	{
		dateFromServer = Date.fixDateString(dateFromServer);
		let dateConverted = new Date(dateFromServer.getUTCFullYear(), dateFromServer.getUTCMonth(), dateFromServer.getUTCDate(), dateFromServer.getUTCHours(), dateFromServer.getUTCMinutes(), dateFromServer.getUTCSeconds());
		return dateConverted;
	}
	else
		return dateFromServer;
}

Date.convertDateFromClient = function (dateFromClient?: Date | null): Date
{
	if (dateFromClient != null)
	{
		dateFromClient = Date.fixDateString(dateFromClient);
		let dateConverted = new Date(Date.UTC(dateFromClient.getFullYear(), dateFromClient.getMonth(), dateFromClient.getDate(), dateFromClient.getHours(), dateFromClient.getMinutes(), dateFromClient.getSeconds()));
		return dateConverted;
	}
	else
		return dateFromClient!;
}

Date.fixDateString = function (dateString?: Date | string | null): Date
{
	if (dateString != null && !(Object.prototype.toString.call(dateString) === '[object Date]'))
	{
		let tempDateString = dateString.toString();
		let dateSplitted = tempDateString.split(/[^0-9]/).toNumberArrayList();
		let date = new Date(dateSplitted[0], dateSplitted[1] - 1, dateSplitted[2], dateSplitted[3], dateSplitted[4], dateSplitted[5]);
		dateString = new Date(date.toString());
	}

	return dateString! as Date;
}

Date.differenceBetweenDatesInMilliseconds = function (firstDate?: Date | null, secondDate?: Date | null): number
{
	if (firstDate == null || secondDate == null)
		return 0;

	let first = this.fixDateString(firstDate);
	let second = this.fixDateString(secondDate);

	let difference = first.getTime() - second.getTime();
	return Math.abs(difference);
}

Date.differenceBetweenDatesInSeconds = function (firstDate?: Date | null, secondDate?: Date | null): number
{
	if (firstDate == null || secondDate == null)
		return 0;

	let first = this.fixDateString(firstDate);
	let second = this.fixDateString(secondDate);

	let difference = first.getTime() - second.getTime();
	let secondsDifference = difference / 1000;
	return Math.abs(secondsDifference);
}

Date.differenceBetweenDatesInMinutes = function (firstDate?: Date | null, secondDate?: Date | null): number
{
	if (firstDate == null || secondDate == null)
		return 0;

	let first = this.fixDateString(firstDate);
	let second = this.fixDateString(secondDate);

	let difference = first.getTime() - second?.getTime();
	let secondsDifference = difference / 1000;
	return Math.abs(secondsDifference / 60);
}

Date.differenceBetweenDatesInDays = function (firstDate?: Date | null, secondDate?: Date | null): number
{
	if (firstDate == null || secondDate == null)
		return 0;

	let first = this.fixDateString(firstDate);
	let second = this.fixDateString(secondDate);

	let difference = first.getTime() - second?.getTime();
	let secondsDifference = difference / 1000;
	return Math.abs(secondsDifference / 60 / 60 / 24);
}

Date.getFirstDayOfWeekByDate = function (dateToCheck: Date): Date
{
	let date = new Date(dateToCheck);
	let day = date.getDay(),
		diff = date.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
	return new Date(date.setDate(diff));
}

Date.getFirstDayOfMonthByDate = function (dateToCheck: Date): Date
{
	let date = new Date(dateToCheck);
	return new Date(date.getFullYear(), date.getMonth(), 1);
}

Date.getLastDayOfMonthByDate = function (dateToCheck: Date): Date
{
	let date = new Date(dateToCheck);
	return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

Date.getFirstDayOfYear = function (): Date
{
	return new Date(Date.today().getFullYear(), 0, 1);
}

Date.getLastDayOfYear = function (): Date
{
	return new Date(Date.today().getFullYear(), 11, 31);
}

Date.equals = function (firstDate: Date, secondDate: Date, ignoreHours = false): boolean
{
	if (ignoreHours)
	{
		let first = new Date(firstDate);
		let second = new Date(secondDate);
		first.setHours(0, 0, 0, 0);
		second.setHours(0, 0, 0, 0);
		return first.getTime() == second.getTime();
	}
	else
		return firstDate.getTime() === secondDate.getTime();
}

Date.getSundaysOfMonthByDate = function (dateToCheck: Date): Date[]
{
	dateToCheck = new Date(dateToCheck);
	let yearToCheck = dateToCheck.getFullYear();
	let monthToCheck = dateToCheck.getMonth();

	let day = 1;
	let sundays: Date[] = [];
	let date = new Date(yearToCheck, monthToCheck, day);

	while (date.getMonth() === monthToCheck)
	{
		if (date.getDay() === 0) // Sunday
			sundays.push(date);

		day += 1;
		date = new Date(yearToCheck, monthToCheck, day);
	}
	return sundays;
}

Date.getMonthNameByDate = function (date: Date): string
{
	date = this.fixDateString(date);
	return date.toLocaleString('it-IT', { month: 'long' });
}

Date.getDaysInMonth = function (date: Date): number
{
	return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

Date.arePeriodsOverlapped = function (startA: Date, endA: Date, startB: Date, endB: Date, equal = false)
{
	return (!equal) ? (startA.getTime() < endB.getTime() && endA.getTime() > startB.getTime()) : (startA.getTime() <= endB.getTime() && endA.getTime() >= startB.getTime());
}

Date.isValidDate = function (object: any): boolean
{
	if (Object.prototype.toString.call(object) === "[object Date]")
	{
		if (isNaN(object.getTime()))
			return false;
		else
			return true;
	}
	else
		return false;
}

Date.today = function (): Date
{
	let date = new Date();
	date.setHours(0,0,0,0)
	return date;
}
//#endregion

//#region
Date.prototype.toItalyShortString = function (this): string
{
	// date.toLocaleDateString("it-IT", { weekDay: 'short' })
	let yyyy: number = this.getFullYear();
	let mm: number = this.getMonth() + 1;
	let dd: number = this.getDate();
	let hh: number = this.getHours();
	let MM: number = this.getMinutes();

	let dateString: string = [(dd > 9 ? '' : '0') + dd, (mm > 9 ? '' : '0') + mm, yyyy].join('/');
	return dateString;
}

Date.prototype.toItalyString = function (mode?: vr.DateModeEnum, showSeconds = false): string
{
	let dateOptions: Intl.DateTimeFormatOptions = {};
	switch (mode)
	{
		case vr.DateModeEnum.Date:
			{
				dateOptions = {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour12: false
				}
			}
			break;
		case vr.DateModeEnum.LongWeekDate:
			{
				dateOptions = {
					weekday: 'long',
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour12: false
				}
			}
			break;
		case vr.DateModeEnum.ShortWeekDate:
			{
				dateOptions = {
					weekday: 'short',
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour12: false
				}
			}
			break;
		case vr.DateModeEnum.LongDate:
			{
				dateOptions = {
					weekday: 'long',
					year: 'numeric',
					month: 'long',
					day: '2-digit',
					hour12: false
				}
			}
			break;
		case vr.DateModeEnum.DateTime:
			{
				dateOptions = {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour: "2-digit",
					minute: "2-digit",
					hour12: false
				}

				if (showSeconds)
					dateOptions.second = "2-digit";
			}
			break;
		case vr.DateModeEnum.LongDateTime:
			{
				dateOptions = {
					weekday: 'long',
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
					hour: "2-digit",
					minute: "2-digit",
					hour12: false
				}

				if (showSeconds)
					dateOptions.second = "2-digit";
			}
			break;
		case vr.DateModeEnum.Time:
			{
				dateOptions = {
					hour: "2-digit",
					minute: "2-digit",
					hour12: false
				}

				if (showSeconds)
					dateOptions.second = "2-digit";
			}
			break;
	}
	return this.formatString(dateOptions, "it");
}

Date.prototype.formatString = function (options: Intl.DateTimeFormatOptions, language?: string[] | string): string
{
	let dateFormatter = new Intl.DateTimeFormat((language == null) ? "it" : language, options);
	let dateString = dateFormatter.format(this).capitalize();
	return dateString;
}

Date.prototype.toLongDateString = function ()
{
	let dateOptions: any = {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: '2-digit',
		hour12: false
	}
	let dateFormatter = new Intl.DateTimeFormat(/*navigator.language*/"it", dateOptions);
	return dateFormatter.format(this).capitalize();
}

Date.prototype.toTimeString = function (seconds?: boolean): string
{
	let hh: number = this.getHours();
	let MM: number = this.getMinutes();
	let ss: number = this.getSeconds();

	let dateString: string = [(hh > 9 ? '' : '0') + hh, (MM > 9 ? '' : '0') + MM].join(':');
	if (seconds === true)
		dateString += ":" + (ss > 9 ? '' : '0') + ss;

	return dateString;
}

Date.prototype.addYears = function (years: number): Date
{
	let date: Date = new Date(this.valueOf());
	date.setFullYear(date.getFullYear() + years);
	return date;
}

Date.prototype.addMonths = function (months: number): Date
{
	let february = new Date(this.getFullYear(), 1, 1);
	let februaryDays = Date.getDaysInMonth(february);

	let date: Date = new Date(this.valueOf());
	let newMonth = date.getMonth() + months;
	if (newMonth == 1 && date.getDate() > februaryDays) // February
		date.setDate(februaryDays);

	date.setMonth(newMonth);
	return date;
}

Date.prototype.addDays = function (days: number): Date
{
	let date: Date = new Date(this.valueOf());
	date.setDate(date.getDate() + days);
	return date;
}

Date.prototype.addHours = function (hours: number): Date
{
	this.setTime(this.getTime() + (hours * 60 * 60 * 1000));
	return this;
}

Date.prototype.addMinutes = function (minutes: number): Date
{
	this.setTime(this.getTime() + (minutes * 60 * 1000));
	return this;
}

Date.prototype.addSeconds = function (seconds: number): Date
{
	this.setTime(this.getTime() + (seconds * 1000));
	return this;
}

Date.prototype.isBetween = function (firstDate: Date, secondDate: Date): boolean
{
	let millisecondsFirstDate = firstDate.getTime();
	let millisecondsSecondDate = secondDate.getTime();
	let millisecondsDateToCheck = this.getTime();

	if (millisecondsDateToCheck >= millisecondsFirstDate && millisecondsDateToCheck <= millisecondsSecondDate)
		return true;

	return false;
}

Date.prototype.getMonthBegin = function (): Date
{
	let copy = new Date(this);
	copy.setDate(1);
	return copy;
}

Date.prototype.isLessThan = function (date: Date, equals = false): boolean
{
	if (equals)
		return (this.getTime() <= date.getTime());
	else
		return (this.getTime() < date.getTime());
}

Date.prototype.isGreaterThan = function (date: Date, equals = false): boolean
{
	if (equals)
		return (this.getTime() >= date.getTime());
	else
		return (this.getTime() > date.getTime());
}

Date.prototype.isEqualsTo = function (date: Date, ignoreHours = false): boolean
{
	if (ignoreHours)
	{
		let first = new Date(this);
		let second = new Date(date);
		first.setHours(0, 0, 0, 0);
		second.setHours(0, 0, 0, 0);
		return first.getTime() == second.getTime();
	}
	else
		return (this.getTime() == date.getTime());
}

Date.prototype.toVrDateTime = function ()
{
	if (this != null)
	{
		let date = Date.fixDateString(this);
		if (Date.isValidDate(date))
		{
			return {
				day: date!.getDate(),
				month: date!.getMonth() + 1,
				year: date!.getFullYear(),
				hours: date!.getHours(),
				minutes: date!.getMinutes(),
				seconds: date!.getSeconds(),
				milliseconds: date!.getMilliseconds()
			}
		}
	}
	return {} as any;
}

Date.prototype.toVrString = function (mode?: vr.DateModeEnum, showSeconds = false)
{
	let yyyy: number = this.getFullYear();
	let mm: number = this.getMonth() + 1;
	let dd: number = this.getDate();
	let hh: number = this.getHours();
	let MM: number = this.getMinutes();
	let ss: number = this.getSeconds();

	let dateString = [yyyy, String(mm).padStart(2, '0'), String(dd).padStart(2, '0')].join('-');
	let timeString: string = [String(hh).padStart(2, '0'), String(MM).padStart(2, '0')].join(':');
	if (showSeconds)
		timeString += ":" + String(ss).padStart(2, '0');

	if (mode == null) mode = vr.DateModeEnum.Date;
	if (mode == vr.DateModeEnum.Date)
		dateString = dateString;
	else if (mode == vr.DateModeEnum.DateTime)
		dateString = dateString + " " + timeString;
	else if (mode == vr.DateModeEnum.Time)
		dateString = timeString;

	return dateString;
}
//#endregion

